/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {useState, useEffect, useMemo} from 'react';
import { useStandUp } from '../contexts/StandUpContext';
import {
    X,
    User,
    Users,
    Plug,
    Keyboard,
    ChevronRight,
    LogOut,
    Globe,
    Shield,
    Check,
    Mail,
    Info,
    RefreshCw,
    Loader2,
    LayoutDashboard,
    CheckSquare,
    Boxes,
    Link as LinkIcon,
    Plus,
    Cpu,
    Download,
    AlertTriangle,
    Trash2,
    Settings,
    UserX,
    FileText,
    Building2,
    Search
} from 'lucide-react';
import { IntegrationProvider, Team } from '../types';
import { desktop, isDesktop, isMobile, settings } from '../platform';
import AlertModal from './AlertModal';
import SettingsModalMobile from './SettingsModalMobile';

const SettingsModal: React.FC<{ initialTab?: 'profile' | 'teams' | 'integrations' | 'shortcuts' | 'system', onLogout?: () => void }> = ({ initialTab, onLogout }) => {
    const { 
        isSettingsModalOpen, 
        closeSettingsModal, 
        userProfile, 
        myOrgTeams, 
        organization,
        logout, 
        toggleTeam,
        getTeamColor,
        deleteUserAccount,
        isTauri,
        isMembersModalOpen,
        isTeamSettingsModalOpen,
        showToast,
        updateOrganization,
        fetchDiscoverableTeams,
        requestTeamAccess,
        fetchProjectMappingsForTeams,
        toggleProviderSync,
        syncIntegrations,
        availableProviders,
        openTeamModal,
        joinTeam,
        openMembersModal,
        openTeamSettingsModal,
        setAllTeams
    } = useStandUp();

    const [activeTab, setActiveTab] = useState<'profile' | 'teams' | 'integrations' | 'shortcuts' | 'system'>('profile');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isFinalConfirmOpen, setIsFinalConfirmOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Sync activeTab with initialTab when modal opens
    useEffect(() => {
        if (isSettingsModalOpen) {
            if (initialTab) {
                setActiveTab(initialTab);
            } else {
                // If profile is hidden, default to teams
                setActiveTab(settings.shouldShowProfile() ? 'profile' : 'teams');
            }
        }
    }, [isSettingsModalOpen, initialTab]);

    const handleDeleteAccount = async () => {
        setIsDeleteModalOpen(true);
    };

    const confirmDeleteAccount = () => {
        setIsDeleteModalOpen(false);
        setIsFinalConfirmOpen(true);
    };

    const finalConfirmDeleteAccount = async () => {
        setIsFinalConfirmOpen(false);
        setIsDeleting(true);
        console.log("[Auth] Initiating permanent account deletion...");
        try {
            await deleteUserAccount();
            closeSettingsModal();
            // Auth context will handle redirection/cleanup
        } catch (e: any) {
            console.error("Delete failed", e);
            showToast("Failed to delete account: " + e.message, "error");
            setIsDeleting(false);
        }
    };

    // Handle Escape Key
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                // IMPORTANT: Only close if NO other sub-modals are open on top
                if (isSettingsModalOpen && !isMembersModalOpen && !isTeamSettingsModalOpen && !isDeleteModalOpen && !isFinalConfirmOpen) {
                    e.preventDefault();
                    e.stopPropagation();
                    closeSettingsModal();
                }
            }
        };
        if (isSettingsModalOpen) window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isSettingsModalOpen, isMembersModalOpen, isTeamSettingsModalOpen, isDeleteModalOpen, isFinalConfirmOpen, closeSettingsModal]);

    if (!isSettingsModalOpen || !userProfile) return null;

    if (isMobile()) {
        return (
            <SettingsModalMobile 
                activeTab={activeTab} 
                setActiveTab={setActiveTab as any} 
                userProfile={userProfile}
                onClose={closeSettingsModal}
                onLogout={onLogout || (() => { logout(); closeSettingsModal(); })}
                onDeleteAccount={handleDeleteAccount}
                getTeamColor={getTeamColor}
                toggleTeam={toggleTeam}
                tabs={{
                    profile: <ProfileTab userProfile={userProfile} onDeleteAccount={handleDeleteAccount} />,
                    teams: <TeamsTab userProfile={userProfile} getTeamColor={getTeamColor} toggleTeam={toggleTeam} />,
                    integrations: <IntegrationsTab userProfile={userProfile} />,
                    shortcuts: <ShortcutsTab isTauri={false} />
                }}
            />
        );
    }

    const hasTeamsInOrg = myOrgTeams && myOrgTeams.length > 0;

    const tabs = [
        ...(settings.shouldShowProfile() ? [{ id: 'profile', label: 'Profile', icon: User }] : []),
        { id: 'teams', label: 'My Teams', icon: Users }, // ALWAYS SHOW
        ...(hasTeamsInOrg && organization && organization.type !== 'personal' ? [
            { id: 'integrations', label: 'Integrations', icon: Plug },
        ] : []),
        ...(settings.shouldShowShortcuts() ? [{ id: 'shortcuts', label: 'Shortcuts', icon: Keyboard }] : []),
    ];

    if (isDesktop()) {
        tabs.push({ id: 'system', label: 'System', icon: Cpu });
    }

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-gray-900 w-full max-w-4xl h-[600px] rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 flex overflow-hidden animate-in zoom-in-95 duration-200">
                
                {/* Sidebar */}
                <div className="w-64 bg-gray-50 dark:bg-gray-800/50 border-r border-gray-200 dark:border-gray-800 flex flex-col">
                    <div className="p-6">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Settings</h2>
                    </div>

                    <nav className="flex-1 px-3 space-y-1">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id as any)}
                                className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                                    activeTab === tab.id 
                                    ? 'bg-brand-500 text-white shadow-lg shadow-brand-500/20' 
                                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                                }`}
                            >
                                <tab.icon size={18} />
                                {tab.label}
                            </button>
                        ))}
                    </nav>

                    {settings.shouldShowProfile() && (
                        <div className="p-4 border-t border-gray-200 dark:border-gray-800">
                            <button 
                                onClick={() => { 
                                    if (onLogout) {
                                        onLogout();
                                        closeSettingsModal();
                                    } else {
                                        logout(); 
                                        closeSettingsModal(); 
                                    }
                                }}
                                className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                            >
                                <LogOut size={18} />
                                Sign Out
                            </button>
                        </div>
                    )}
                </div>

                {/* Content Area */}
                <div className="flex-1 flex flex-col min-w-0 bg-white dark:bg-gray-900">
                    <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
                        <span className="text-sm font-bold text-gray-400 uppercase tracking-widest px-2">
                            {tabs.find(t => t.id === activeTab)?.label}
                        </span>
                        <button onClick={closeSettingsModal} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full text-gray-500 transition-colors">
                            <X size={20} />
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-8">
                        {activeTab === 'profile' && <ProfileTab userProfile={userProfile} onDeleteAccount={handleDeleteAccount} />}
                        {activeTab === 'teams' && <TeamsTab userProfile={userProfile} getTeamColor={getTeamColor} toggleTeam={toggleTeam} />}
                        {activeTab === 'integrations' && <IntegrationsTab userProfile={userProfile} />}
                        {activeTab === 'shortcuts' && <ShortcutsTab isTauri={isTauri} />}
                        {activeTab === 'system' && <SystemTab />}
                    </div>
                </div>
            </div>

            <AlertModal 
                isOpen={isDeleteModalOpen}
                title="Delete Account"
                message="Are you sure you want to delete your account? This will delete all your tasks and remove you from all teams. This action cannot be undone."
                confirmText="Continue"
                isDestructive={true}
                onConfirm={confirmDeleteAccount}
                onCancel={() => setIsDeleteModalOpen(false)}
            />

            <AlertModal 
                isOpen={isFinalConfirmOpen}
                title="Final Confirmation"
                message="Are you sure? All your data will be permanently erased. There is no going back."
                confirmText="Delete Everything"
                isDestructive={true}
                onConfirm={finalConfirmDeleteAccount}
                onCancel={() => setIsFinalConfirmOpen(false)}
            />
        </div>
    );
};

// --- SUB-COMPONENTS ---

const SystemTab: React.FC = () => {
    const { 
        updateStatus, 
        updateError, 
        availableUpdate, 
        checkForUpdates, 
        installUpdate, 
        restartApp,
        showToast
    } = useStandUp();
    
    const [appVersion, setAppVersion] = useState('');
    const [isExporting, setIsExporting] = useState(false);

    useEffect(() => {
        if (isDesktop()) {
            desktop.getVersion().then(setAppVersion).catch(() => setAppVersion('Unknown'));
        }
    }, []);

    const handleExportLogs = async () => {
        setIsExporting(true);
        try {
            const baseLogPath = await desktop.appLogDir();
            const appName = await desktop.getName();
            const fullFilePath = await desktop.joinPath(baseLogPath, `${appName}.log`);
            
            try {
                // Try to highlight the specific log file (best UX)
                await desktop.revealItemInDir(fullFilePath);
            } catch (innerErr) {
                // Fallback: just open the generic log folder if specific file not found
                await desktop.revealItem(baseLogPath);
            }
            showToast("Opening log location...");
        } catch (e) {
            console.error(e);
            showToast("Failed to locate logs", "error");
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-300">
            <div className="p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-sm">
                <div className="flex items-center gap-4 mb-6">
                    <div className="p-3 bg-brand-50 dark:bg-brand-900/20 rounded-xl text-brand-600 dark:text-brand-400">
                        <Cpu size={32} />
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white">System Status</h3>
                        <p className="text-gray-500 text-sm">StandUp Desktop Client v{appVersion}</p>
                    </div>
                </div>

                <div className="space-y-4">
                    {/* Status Display */}
                    <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 flex items-center justify-between">
                        <div>
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Update Status</p>
                            
                            {updateStatus === 'idle' && <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Ready to check.</span>}
                            {updateStatus === 'checking' && <span className="text-sm font-medium text-brand-600 flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Checking...</span>}
                            {updateStatus === 'uptodate' && <span className="text-sm font-medium text-emerald-600 flex items-center gap-2"><Check size={14}/> You are on the latest version.</span>}
                            {updateStatus === 'available' && <span className="text-sm font-bold text-brand-600">New version {availableUpdate?.version} available!</span>}
                            {updateStatus === 'downloading' && <span className="text-sm font-medium text-brand-600 flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Downloading & Installing...</span>}
                            {updateStatus === 'ready' && <span className="text-sm font-bold text-emerald-600">Update installed. Restart required.</span>}
                            {updateStatus === 'error' && <span className="text-sm font-bold text-red-500 flex items-center gap-2"><AlertTriangle size={14}/> {updateError}</span>}
                        </div>

                        {/* Actions */}
                        <div>
                            {(updateStatus === 'idle' || updateStatus === 'uptodate' || updateStatus === 'error') ? (
                                <button 
                                    onClick={() => checkForUpdates()}
                                    className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg text-sm font-bold transition-all shadow-sm active:scale-95"
                                >
                                    Check for Updates
                                </button>
                            ) : null}

                            {updateStatus === 'available' && (
                                <button 
                                    onClick={() => installUpdate()}
                                    className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-bold transition-all shadow-lg shadow-brand-500/20 active:scale-95 flex items-center gap-2"
                                >
                                    <Download size={16} />
                                    Download & Install
                                </button>
                            )}

                            {updateStatus === 'ready' && (
                                <button 
                                    onClick={() => restartApp()}
                                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-bold transition-all shadow-lg shadow-emerald-500/20 active:scale-95 flex items-center gap-2"
                                >
                                    <RefreshCw size={16} />
                                    Restart Now
                                </button>
                            )}
                        </div>
                    </div>

                    {updateStatus === 'available' && availableUpdate?.body && (
                        <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800 rounded-xl">
                            <p className="text-xs font-bold text-blue-600 dark:text-blue-400 mb-2 uppercase tracking-wide">Release Notes</p>
                            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{availableUpdate.body}</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Diagnostic Section */}
            <div className="p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-sm">
                <div className="flex items-center gap-4 mb-6">
                    <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl text-indigo-600 dark:text-indigo-400">
                        <FileText size={32} />
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white">Diagnostic Logs</h3>
                        <p className="text-gray-500 text-sm">Access system logs for support.</p>
                    </div>
                </div>
                
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                    If you're experiencing issues with syncing or notifications, our support team might ask for your system logs.
                </p>

                <button 
                    onClick={handleExportLogs}
                    disabled={isExporting}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-white rounded-xl text-sm font-bold hover:bg-gray-100 dark:hover:bg-gray-700 transition-all active:scale-95 shadow-sm"
                >
                    {isExporting ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
                    Show Log Location
                </button>
            </div>
        </div>
    );
};

const ProfileTab: React.FC<{ userProfile: any, onDeleteAccount: () => void }> = ({ userProfile, onDeleteAccount }) => {
    const { organization, showToast, updateOrganization } = useStandUp();
    const [name, setName] = useState(organization?.name || '');
    const [isSaving, setIsLoading] = useState(false);

    const handleSaveWorkspaceName = async () => {
        if (!name.trim() || !organization || name === organization.name) return;
        setIsLoading(true);
        try {
            await updateOrganization(organization.id, { name: name.trim() });
            showToast("Workspace renamed!");
        } catch (e) {
            console.error(e);
            showToast("Failed to rename workspace", "error");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-300">
            <div className="flex items-center gap-6">
                <img 
                    src={userProfile.photoURL || `https://ui-avatars.com/api/?name=${userProfile.displayName}`} 
                    className="w-20 h-20 rounded-2xl object-cover ring-4 ring-gray-100 dark:ring-gray-800"
                    alt=""
                />
                <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">{userProfile.displayName}</h3>
                    <p className="text-gray-500 dark:text-gray-400">{userProfile.email}</p>
                </div>
            </div>

            <div className="grid gap-6">
                {/* Personal Workspace Name Edit */}
                {organization?.type === 'personal' && (
                    <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                            <Building2 size={14} /> Workspace Name
                        </label>
                        <div className="flex gap-2">
                            <input 
                                type="text"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                className="flex-1 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-2.5 text-sm focus:ring-2 ring-brand-500 outline-none dark:text-white transition-all"
                                placeholder="Workspace Name"
                            />
                            <button 
                                onClick={handleSaveWorkspaceName}
                                disabled={isSaving || !name.trim() || name === organization.name}
                                className="px-6 bg-brand-600 hover:bg-brand-700 text-white text-xs font-bold rounded-xl transition-all disabled:opacity-50 shadow-lg shadow-brand-500/20 active:scale-95"
                            >
                                {isSaving ? <Loader2 size={16} className="animate-spin" /> : "Save"}
                            </button>
                        </div>
                        <p className="text-[10px] text-gray-500 italic">This name is only visible to you.</p>
                    </div>
                )}

                <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                        <Globe size={14} /> Timezone
                    </label>
                    <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 text-sm">
                        {userProfile.timezone || 'UTC'}
                    </div>
                    <p className="text-[10px] text-gray-500 italic">Detected automatically from your system.</p>
                </div>
            </div>

            <div className="pt-8 border-t border-gray-100 dark:border-gray-800">
                <button
                    onClick={onDeleteAccount}
                    className="flex items-center gap-2 px-4 py-2 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/30 text-red-600 rounded-xl text-xs font-bold transition-all"
                >
                    <UserX size={16} />
                    Delete Account
                </button>
            </div>
        </div>
    );
};

const TeamsTab: React.FC<{ userProfile: any, getTeamColor: any, toggleTeam: any }> = ({ userProfile, getTeamColor, toggleTeam }) => {
    const { organization, myOrgTeams, openMembersModal, openTeamSettingsModal, setAllTeams, openTeamModal, joinTeam, showToast, fetchDiscoverableTeams, requestTeamAccess } = useStandUp();
    const [subTab, setSubTab] = useState<'my' | 'discover'>('my');
    const [discoverTeams, setDiscoverTeams] = useState<Team[]>([]);
    const [loadingDiscover, setLoadingDiscover] = useState(false);
    const [processingTeamId, setProcessingTeamId] = useState<string | null>(null); // NEW
    const [searchQuery, setSearchQuery] = useState('');

    const activeIds = userProfile.activeTeamIds || [];
    const areAllSelected = activeIds.length === (myOrgTeams?.length || 0);

    const handleSelectAll = () => {
        const allIds = myOrgTeams?.map((t: any) => t.id) || [];
        setAllTeams(allIds);
    };

    const handleDeselectAll = () => {
        setAllTeams([]);
    };

    // --- DISCOVER TEAMS LOGIC ---
    useEffect(() => {
        if (subTab !== 'discover' || !organization?.id) return;

        const fetchDiscoverable = async () => {
            setLoadingDiscover(true);
            try {
                const discoverable = await fetchDiscoverableTeams(organization.id, userProfile.teamIds || []);
                setDiscoverTeams(discoverable);
            } catch (e) {
                console.error("Discovery error:", e);
            } finally {
                setLoadingDiscover(false);
            }
        };
        fetchDiscoverable();
    }, [subTab, organization?.id, JSON.stringify(userProfile.teamIds)]);

    const handleJoinAction = async (team: Team) => {
        setProcessingTeamId(team.id);
        try {
            if (team.visibility === 'public') {
                await joinTeam(team.id);
                showToast(`Joined ${team.name}!`);
            } else if (team.visibility === 'protected') {
                await requestTeamAccess(team.id, userProfile.id);
                
                // OPTIMISTIC UI: Update local state immediately so button changes
                setDiscoverTeams(prev => prev.map(t => {
                    if (t.id === team.id) {
                        return { ...t, pendingRequests: [...(t.pendingRequests || []), userProfile.id] };
                    }
                    return t;
                }));

                showToast("Access requested!");
            }
        } catch (e: any) {
            showToast(e.message || "Action failed", "error");
        } finally {
            setProcessingTeamId(null);
        }
    };

    const sortedTeams = useMemo(() => {
        const base = subTab === 'my' ? myOrgTeams : discoverTeams;
        return [...(base || [])]
            .filter(t => t.name.toLowerCase().includes(searchQuery.toLowerCase()))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [subTab, myOrgTeams, discoverTeams, searchQuery]);
    
    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
            {/* Sub-navigation */}
            <div className="flex items-center gap-4 border-b border-gray-100 dark:border-gray-800 pb-2">
                <button 
                    onClick={() => setSubTab('my')}
                    className={`pb-2 px-1 text-sm font-bold transition-all relative ${subTab === 'my' ? 'text-brand-600 dark:text-brand-400' : 'text-gray-400 hover:text-gray-600'}`}
                >
                    Joined Teams
                    {subTab === 'my' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-500 rounded-full" />}
                </button>
                <button 
                    onClick={() => setSubTab('discover')}
                    className={`pb-2 px-1 text-sm font-bold transition-all relative ${subTab === 'discover' ? 'text-brand-600 dark:text-brand-400' : 'text-gray-400 hover:text-gray-600'}`}
                >
                    Discover
                    {subTab === 'discover' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-500 rounded-full" />}
                </button>
                
                <div className="flex-1" />

                <button 
                    onClick={() => openTeamModal({ type: 'create' })}
                    className="flex items-center gap-2 px-3 py-1.5 bg-brand-50 dark:bg-brand-900/20 text-brand-600 dark:text-brand-400 hover:bg-brand-100 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all"
                >
                    <Plus size={14} /> Create New
                </button>
            </div>

            {/* Search within teams */}
            <div className="relative group">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-brand-500 transition-colors" />
                <input 
                    type="text"
                    placeholder={subTab === 'my' ? "Filter your teams..." : "Search all organization teams..."}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:ring-2 ring-brand-500/20 outline-none transition-all dark:text-white"
                />
            </div>

            {subTab === 'my' && sortedTeams.length > 0 && (
                <div className="flex items-center justify-between px-1">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Visibility Control</p>
                    <button
                        onClick={areAllSelected ? handleDeselectAll : handleSelectAll}
                        className="text-[10px] font-bold text-brand-600 dark:text-brand-400 hover:underline transition-all"
                    >
                        {areAllSelected ? 'Deselect All' : 'Select All'}
                    </button>
                </div>
            )}

            <div className="grid gap-3">
                {loadingDiscover ? (
                    <div className="py-20 flex justify-center"><Loader2 size={24} className="animate-spin text-brand-500" /></div>
                ) : sortedTeams.length === 0 ? (
                    <div className="flex flex-col items-center justify-center p-12 bg-gray-50 dark:bg-gray-800/50 border border-dashed border-gray-200 dark:border-gray-700 rounded-2xl text-center">
                        <div className="w-16 h-16 bg-white dark:bg-gray-800 rounded-2xl shadow-sm flex items-center justify-center text-gray-400 mb-4">
                            {subTab === 'my' ? <Users size={32} /> : <Search size={32} />}
                        </div>
                        <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                            {subTab === 'my' ? "No joined teams" : "No teams found"}
                        </h4>
                        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs mb-6">
                            {subTab === 'my' 
                                ? "You haven't joined any teams in this organization yet."
                                : "Try searching for a different team name or check back later."}
                        </p>
                        {subTab === 'my' && (
                            <button 
                                onClick={() => setSubTab('discover')}
                                className="px-6 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-xl text-xs font-bold shadow-lg shadow-brand-500/20 transition-all active:scale-95"
                            >
                                Browse Teams
                            </button>
                        )}
                    </div>
                ) : (
                    sortedTeams.map((team: any) => {
                    const color = getTeamColor(team.id);
                    const isActive = userProfile.activeTeamIds?.includes(team.id);
                    const isJoined = (userProfile.teamIds || []).includes(team.id);
                    
                    // Look up role in userProfile (where memberships are stored)
                    const membership = userProfile.teams?.find((t: any) => t.teamId === team.id);
                    const role = membership?.role || 'engineer';

                    return (
                        <div key={team.id} className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm">
                            <div className="flex items-center gap-4 min-w-0">
                                <div className={`w-10 h-10 rounded-xl ${color.style} flex items-center justify-center font-bold text-lg shrink-0`}>
                                    {team.name.charAt(0)}
                                </div>
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <h4 className="font-bold text-gray-900 dark:text-white truncate">{team.name}</h4>
                                        {team.visibility === 'protected' && <Shield size={12} className="text-amber-500" />}
                                    </div>
                                    <div className="flex items-center gap-2 mt-0.5">
                                        {isJoined ? (
                                            <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase">
                                                {role}
                                            </span>
                                        ) : (
                                            <span className="text-[10px] text-gray-400">
                                                {team.members?.length || 0} members
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            <div className="flex items-center gap-2 shrink-0">
                                {isJoined ? (
                                    <>
                                        {role === 'manager' && (
                                            <button
                                                onClick={() => openTeamSettingsModal(team.id)}
                                                className="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-400 hover:text-brand-500 hover:border-brand-500 rounded-lg transition-all shadow-sm"
                                                title="Team Settings"
                                            >
                                                <Settings size={16} />
                                            </button>
                                        )}

                                        <button
                                            onClick={() => openMembersModal(team.id)}
                                            className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:text-brand-500 hover:border-brand-500 rounded-lg text-xs font-bold transition-all shadow-sm"
                                            title="Manage Members"
                                        >
                                            <Users size={16} />
                                            <span className="hidden md:inline">Members</span>
                                        </button>
                                        
                                        <button 
                                            onClick={() => toggleTeam(team.id)}
                                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all shadow-sm ${
                                                isActive 
                                                ? 'bg-emerald-600 text-white' 
                                                : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                                            }`}
                                        >
                                            {isActive ? <Check size={14} /> : null}
                                            <span className="hidden md:inline">{isActive ? 'Active' : 'Show'}</span>
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        onClick={() => handleJoinAction(team)}
                                        disabled={processingTeamId === team.id || (team.pendingRequests || []).includes(userProfile.id)}
                                        className={`px-6 py-2 rounded-lg text-xs font-bold transition-all shadow-sm flex items-center justify-center gap-2 min-w-[120px] ${
                                            (team.pendingRequests || []).includes(userProfile.id)
                                            ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                                            : team.visibility === 'public'
                                                ? 'bg-brand-600 hover:bg-brand-700 text-white'
                                                : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                                        }`}
                                    >
                                        {processingTeamId === team.id ? (
                                            <Loader2 size={14} className="animate-spin" />
                                        ) : (team.pendingRequests || []).includes(userProfile.id) ? (
                                            'Request Sent'
                                        ) : team.visibility === 'public' ? (
                                            'Join Team'
                                        ) : (
                                            'Request Access'
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                }))}
            </div>
        </div>
    );
};

const IntegrationsTab: React.FC<{ userProfile: any }> = ({ userProfile }) => {
    const { organization, myOrgTeams, toggleProviderSync, syncIntegrations, showToast, availableProviders, fetchProjectMappingsForTeams } = useStandUp();
    const [isSyncing, setIsSyncing] = useState(false);
    const [projectMappings, setProjectMappings] = useState<any[]>([]);
    const [loadingMappings, setLoadingMappings] = useState(false);

    const getIcon = (type: string) => {
        if (type?.startsWith('http')) {
            return <img src={type} className="w-6 h-6 object-contain filter brightness-0 invert" alt="" />;
        }

        switch (type) {
            case 'plug': return <Plug size={24} />;
            case 'layout': return <LayoutDashboard size={24} />;
            case 'check-square': return <CheckSquare size={24} />;
            case 'boxes': return <Boxes size={24} />;
            case 'link': return <LinkIcon size={24} />;
            case 'globe': return <Globe size={24} />;
            default: return <Plug size={24} />;
        }
    };

    useEffect(() => {
        const fetchMappings = async () => {
            setLoadingMappings(true);
            const teamIds = myOrgTeams.map(t => t.id) || [];
            if (teamIds.length === 0) {
                setProjectMappings([]);
                setLoadingMappings(false);
                return;
            }

            if (!organization?.id) return;
            try {
                const mappings = await fetchProjectMappingsForTeams(organization.id, teamIds);
                setProjectMappings(mappings);
            } catch (e) {
                console.error("Failed to fetch mappings", e);
            } finally {
                setLoadingMappings(false);
            }
        };
        fetchMappings();
    }, [myOrgTeams]);

    const handleManualSync = async () => {
        setIsSyncing(true);
        try {
            await syncIntegrations();
            showToast("Sync completed successfully!");
        } catch (e: any) {
            showToast("Sync failed: " + e.message, "error");
        } finally {
            setIsSyncing(false);
        }
    };
    
    if (availableProviders.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                <Plug size={48} className="mb-4 opacity-20" />
                <p>No integrations configured yet.</p>
            </div>
        );
    }

    return (
        <div className="space-y-10 animate-in fade-in slide-in-from-right-4 duration-300">
            {availableProviders.map(provider => {
                const config = userProfile.integrations?.[provider.id];
                const providerMappings = projectMappings.filter(m => m.provider === provider.id);
                
                // DEDUPLICATE AND SORT ALPHABETICALLY BY NAME
                const sortedProjects = Array.from(
                    new Map(providerMappings.map(m => [`${m.provider}_${m.projectId}`, m])).values()
                ).sort((a, b) => (a.projectName || '').localeCompare(b.projectName || ''));
                
                return (
                    <div key={provider.id} className="space-y-6">
                        {/* Provider Header Card */}
                        <div className="p-6 bg-gradient-to-br from-indigo-500 to-brand-600 rounded-2xl text-white shadow-xl shadow-brand-500/20">
                            <div className="flex items-start justify-between">
                                <div className="space-y-4">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-white/20 backdrop-blur-md rounded-lg text-white">
                                            {getIcon(provider.iconType)}
                                        </div>
                                        <h3 className="text-xl font-bold">{provider.name} Sync</h3>
                                    </div>
                                    <p className="text-indigo-100 text-sm max-w-md leading-relaxed">
                                        {provider.description}
                                    </p>

                                    <button 
                                        onClick={handleManualSync}
                                        disabled={isSyncing}
                                        className="mt-2 flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-xl text-xs font-bold transition-all disabled:opacity-50"
                                    >
                                        {isSyncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                                        Sync Now
                                    </button>
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                    <span className="px-3 py-1 bg-white/20 backdrop-blur-md rounded-full text-[10px] font-bold uppercase tracking-widest">
                                        Active Integration
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Project Toggles for this Provider */}
                        <div className="space-y-4">
                            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest px-1">Active Projects: {provider.name}</h4>
                            <div className="grid gap-3">
                                {sortedProjects.length === 0 && !loadingMappings && (
                                    <p className="text-xs text-gray-500 italic px-1">No projects linked to your teams yet.</p>
                                )}
                                {sortedProjects.map((project: any) => {
                                    const projectKey = `${project.provider}_${project.projectId}`;
                                    const isSynced = !config?.disabledProjectKeys?.includes(projectKey);
                                    const projectName = project.projectName || project.projectId;
                                    return (
                                        <div key={projectKey} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-brand-300 transition-colors">
                                            <div className="flex items-center gap-3">
                                                <div className="w-2 h-2 rounded-full bg-brand-500 shadow-[0_0_8px_rgba(99,102,241,0.5)]"></div>
                                                <span className="font-bold text-gray-700 dark:text-gray-200">{projectName}</span>
                                            </div>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    className="sr-only peer" 
                                                    checked={isSynced}
                                                    onChange={() => toggleProviderSync(provider.id, projectKey)}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-brand-600"></div>
                                            </label>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                );
            })}

            <div className="flex items-center gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/50 rounded-xl text-amber-800 dark:text-amber-200">
                <Info size={18} className="shrink-0" />
                <p className="text-xs">
                    Sync runs automatically every 6 hours. Manual sync triggers an immediate refresh from all providers.
                </p>
            </div>
        </div>
    );
};

const ShortcutsTab: React.FC<{ isTauri: boolean }> = ({ isTauri }) => (
    <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-300">
        <div className="grid gap-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                <div>
                    <h4 className="font-bold text-gray-900 dark:text-white">Quick Toggle Dashboard</h4>
                    <p className="text-xs text-gray-500">Minimize or restore the app instantly.</p>
                </div>
                <div className="flex gap-1">
                    <kbd className="px-2 py-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-xs font-bold shadow-sm">⌘</kbd>
                    <kbd className="px-2 py-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-xs font-bold shadow-sm">⇧</kbd>
                    <kbd className="px-2 py-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-xs font-bold shadow-sm">H</kbd>
                </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                <div>
                    <h4 className="font-bold text-gray-900 dark:text-white">Compact Mode</h4>
                    <p className="text-xs text-gray-500">Switch between full and mini view.</p>
                </div>
                <div className="flex gap-1">
                    <kbd className="px-2 py-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-xs font-bold shadow-sm">⌘</kbd>
                    <kbd className="px-2 py-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-xs font-bold shadow-sm">⇧</kbd>
                    <kbd className="px-2 py-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-xs font-bold shadow-sm">X</kbd>
                </div>
            </div>
        </div>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-xl">
            <p className="text-xs text-blue-800 dark:text-blue-300 flex items-center gap-2">
                <Shield size={14} />
                Global shortcuts work even when StandUp is in the background.
            </p>
        </div>
    </div>
);

export default SettingsModal;
